import React from 'react';
import { Flex } from '@withjoy/joykit';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { DragIcon } from '@assets/icons';
import { DragHandle } from '../SortableCard/SortableCard';
import { VacationRentalTile } from '@shared/components/VacationRentalTile/VacationRentalTile';
import type { VacationRentalAreaData } from '@shared/components/VacationRentalTile/VacationRentalTile.types';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { pxToRem } from '@withjoy/joykit/theme';
import { StatusLabel } from '@shared/components/StatusLabel';

interface SortableVacationRentalTileProps
  extends Readonly<{
    rental: VacationRentalAreaData;
    isDragActive: boolean;
    onView: (rental: VacationRentalAreaData) => void;
    onRemove: (rental: VacationRentalAreaData) => void;
    containerWidth?: number | null;
    isItemDragActive?: boolean;
  }> {}

export const SortableVacationRentalTile = (props: SortableVacationRentalTileProps) => {
  const { rental, isDragActive, onView, onRemove, containerWidth, isItemDragActive } = props;

  const isMobile = useIsMobileScreen();
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();

  // @todo [TRAV-2250] Enable drag-and-drop when backend supports reordering
  const isSortableListEnabled = false;

  return (
    <VacationRentalTile
      role="group"
      cursor="default"
      paddingX={4}
      paddingTop={4}
      paddingBottom={6}
      minWidth="fit-content"
      width={containerWidth ? pxToRem(containerWidth) : '100%'}
    >
      {isSortableListEnabled && (
        <DragHandle
          visibility={isDragActive || isMobile ? 'visible' : 'hidden'}
          cursor={isDragActive ? 'grabbing' : 'grab'}
          {...attributes}
          {...listeners}
          ref={draggableNodeRef}
        >
          <DragIcon
            size={16}
            style={{ transform: 'rotate(90deg)' }}
          />
        </DragHandle>
      )}
      <VacationRentalTile.Header
        menu={
          <VacationRentalTile.Menu
            rental={rental}
            onView={onView}
            onRemove={onRemove}
          />
        }
        distance={
          <Flex
            gap={3}
            alignItems="center"
          >
            <DistanceFromVenue
              distances={rental.distance}
              milesToVenue={rental.distanceFromVenue}
              hasIcon
              size="sm"
            />
            <DistanceStatistics
              distances={rental.distance}
              milesToVenue={rental.distanceFromVenue}
              fontSize={pxToRem(13)}
            />
          </Flex>
        }
      >
        {rental.name}
      </VacationRentalTile.Header>

      {!isItemDragActive && (
        <Flex
          flexDirection="column"
          marginTop={3}
          paddingX={4}
          gap={5}
        >
          {rental.photoUrl && (
            <VacationRentalTile.Photo
              photoUrl={rental.photoUrl}
              name={rental.name}
              borderRadius={pxToRem(12)}
            >
              {rental.bestForLabel && (
                <StatusLabel
                  top={5}
                  right={5}
                  gap={2}
                  position="absolute"
                >
                  <span aria-hidden={true}>{rental.bestForLabel.emoji}</span>
                  {rental.bestForLabel.label}
                </StatusLabel>
              )}
            </VacationRentalTile.Photo>
          )}
          {rental.features.length > 0 && (
            <Flex flexDirection="column">
              {rental.features.map(feature => (
                <VacationRentalTile.Feature
                  key={`${rental.id}-feature-${feature}`}
                  feature={feature}
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </VacationRentalTile>
  );
};
